import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Upload,
  Camera,
  CheckCircle2,
  Clock,
  AlertTriangle,
  X,
  FileText,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const documentTypes = [
  { value: 'national_id', label: 'National ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'driver_license', label: "Driver's License" },
];

export default function Verification() {
  const { user, isOwner } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [documentType, setDocumentType] = useState('national_id');
  const [idDocument, setIdDocument] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [step, setStep] = useState(1);

  // Redirect if not owner
  useEffect(() => {
    if (!isOwner) {
      navigate('/dashboard');
    }
  }, [isOwner, navigate]);

  // Fetch verification status
  useEffect(() => {
    const fetchVerificationStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/verification/status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setVerificationStatus(data);
          // If already verified, show completed state
          if (data.verification?.status === 'approved' || user?.is_verified_owner) {
            setStep(3);
          } else if (data.verification?.status === 'pending') {
            setStep(2);
          }
        }
      } catch (error) {
        console.error('Failed to fetch verification status:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOwner) {
      fetchVerificationStatus();
    }
  }, [isOwner, user]);

  const handleFileSelect = (e, setFile, setPreview) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setPreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/api/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    const data = await response.json();
    return data.url;
  };

  const handleSubmitVerification = async () => {
    if (!idDocument || !selfie) {
      toast.error('Please upload both ID document and selfie');
      return;
    }

    if (!documentType) {
      toast.error('Please select a document type');
      return;
    }

    setSubmitting(true);

    try {
      // Upload files
      const idUrl = await uploadFile(idDocument);
      const selfieUrl = await uploadFile(selfie);

      // Submit verification request
      const response = await fetch(`${API_URL}/api/verification/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          document_type: documentType,
          id_document_url: idUrl,
          selfie_url: selfieUrl,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Submission failed');
      }

      const data = await response.json();
      setVerificationStatus(data);
      setStep(2);
      toast.success('Verification request submitted successfully!');

      // Reset form
      setIdDocument(null);
      setSelfie(null);
      setIdPreview(null);
      setSelfiePreview(null);
    } catch (error) {
      console.error('Verification submission error:', error);
      toast.error(error.message || 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 pb-12">
          <div className="max-w-3xl mx-auto px-6">
            <div className="flex items-center justify-center h-96">
              <Loader2 className="w-8 h-8 text-accent animate-spin" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="pt-20 pb-12">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-2">
              Owner Verification
            </h1>
            <p className="text-slate-500">
              Get verified to build trust with potential renters and unlock exclusive features
            </p>
          </div>

          {/* Steps Indicator */}
          <div className="mb-8 flex items-center justify-between">
            {[
              { num: 1, label: 'Documents' },
              { num: 2, label: 'Review' },
              { num: 3, label: 'Verified' },
            ].map((s, idx) => (
              <div key={s.num} className="flex-1 flex items-center">
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                    step >= s.num
                      ? 'bg-accent text-white'
                      : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {step > s.num ? <CheckCircle2 className="w-5 h-5" /> : s.num}
                </motion.div>
                <span className="ml-3 font-medium text-slate-700">{s.label}</span>
                {idx < 2 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${
                      step > s.num ? 'bg-accent' : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Content Based on Step */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="border-0 shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-accent" />
                    Upload Your Documents
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Document Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Document Type
                    </label>
                    <Select value={documentType} onValueChange={setDocumentType}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {documentTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* ID Document Upload */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      ID Document
                    </label>
                    <div className="relative">
                      {idPreview ? (
                        <motion.div
                          initial={{ scale: 0.95 }}
                          animate={{ scale: 1 }}
                          className="relative w-full aspect-[3/2] rounded-lg overflow-hidden border-2 border-accent border-dashed bg-slate-50"
                        >
                          <img
                            src={idPreview}
                            alt="ID Document Preview"
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => {
                              setIdDocument(null);
                              setIdPreview(null);
                            }}
                            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </motion.div>
                      ) : (
                        <label className="block w-full aspect-[3/2] border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-accent hover:bg-slate-50 transition-colors flex items-center justify-center">
                          <div className="text-center">
                            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-slate-700">Click to upload</p>
                            <p className="text-xs text-slate-500">PNG, JPG up to 5MB</p>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileSelect(e, setIdDocument, setIdPreview)}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Selfie Upload */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      Selfie Photo
                    </label>
                    <div className="relative">
                      {selfiePreview ? (
                        <motion.div
                          initial={{ scale: 0.95 }}
                          animate={{ scale: 1 }}
                          className="relative w-full aspect-square rounded-lg overflow-hidden border-2 border-accent border-dashed bg-slate-50 max-w-xs mx-auto"
                        >
                          <img
                            src={selfiePreview}
                            alt="Selfie Preview"
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => {
                              setSelfie(null);
                              setSelfiePreview(null);
                            }}
                            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </motion.div>
                      ) : (
                        <label className="block w-full aspect-square border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-accent hover:bg-slate-50 transition-colors flex items-center justify-center max-w-xs mx-auto">
                          <div className="text-center">
                            <Camera className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-slate-700">Click to upload</p>
                            <p className="text-xs text-slate-500">PNG, JPG up to 5MB</p>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileSelect(e, setSelfie, setSelfiePreview)}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900 mb-1">Verification Tips:</p>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• Ensure ID is clearly visible and not expired</li>
                          <li>• Take selfie with good lighting and clear face</li>
                          <li>• Make sure selfie matches your ID</li>
                          <li>• Verification usually takes 1-2 business days</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex gap-4 pt-6">
                    <Button
                      onClick={() => navigate('/dashboard')}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmitVerification}
                      disabled={!idDocument || !selfie || submitting}
                      className="flex-1 bg-accent hover:bg-accent/90 rounded-full"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit for Review
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Step 2: Pending Review */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="border-0 shadow-soft bg-amber-50 border-l-4 border-amber-400">
                <CardContent className="p-8">
                  <div className="flex gap-6">
                    <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-8 h-8 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-serif text-2xl font-bold text-amber-900 mb-2">
                        Verification Under Review
                      </h2>
                      <p className="text-amber-800 mb-4">
                        Thank you for submitting your verification documents. Our team is reviewing your submission and will get back to you within 1-2 business days.
                      </p>
                      <div className="space-y-2 text-sm text-amber-700">
                        <p>
                          <strong>Submitted on:</strong> {verificationStatus?.verification?.created_at
                            ? new Date(verificationStatus.verification.created_at).toLocaleDateString()
                            : 'Recently'}
                        </p>
                        <p>
                          <strong>Status:</strong> <Badge className="bg-amber-200 text-amber-900">Pending Review</Badge>
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Step 3: Verified */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="border-0 shadow-soft bg-emerald-50 border-l-4 border-emerald-400">
                <CardContent className="p-8">
                  <div className="flex gap-6">
                    <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-serif text-2xl font-bold text-emerald-900 mb-2">
                        Account Verified!
                      </h2>
                      <p className="text-emerald-800 mb-6">
                        Congratulations! Your identity has been verified. Your listings now display a verified badge, increasing trust and credibility with potential renters.
                      </p>
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center gap-3 text-sm text-emerald-700">
                          <CheckCircle2 className="w-5 h-5" />
                          <span>Verified badge on all your listings</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-emerald-700">
                          <CheckCircle2 className="w-5 h-5" />
                          <span>Increased visibility in search results</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-emerald-700">
                          <CheckCircle2 className="w-5 h-5" />
                          <span>Priority support access</span>
                        </div>
                      </div>
                      <Button
                        onClick={() => navigate('/dashboard')}
                        className="bg-emerald-600 hover:bg-emerald-700 rounded-full"
                      >
                        Back to Dashboard
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
